import { CSSProperties, type ReactNode, useRef, useState } from 'react';
import { Input as MantineInput } from '@mantine/core';
import { useId, useUncontrolled } from '@mantine/hooks';
import { KeyCode } from 'ag-grid-community';
import clsx from 'clsx';

import { ClearButton } from '../clearbutton/ClearButton';
import { FormLabel } from '../formlabel/FormLabel';
import { LoadingIndicator } from '../loading-indicator/LoadingIndicator';

import styles from './input.module.scss';

export type InputProps = {
  'data-testid'?: string;
  disabled?: boolean;
  defaultValue?: string;
  clearable?: boolean;
  error?: string;
  inlineError?: boolean;
  label?: ReactNode | ReactNode[];
  leftSection?: ReactNode;
  rightSection?: ReactNode;
  loading?: boolean;
  readOnly?: boolean;
  onChange?: (value: string) => void;
  onCommit?: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  value?: string;
  style?: CSSProperties;
  tableLayout?: boolean;
};

export function Input({
  clearable,
  'data-testid': dataTestId = 'text-input',
  defaultValue,
  disabled,
  readOnly,
  error,
  inlineError,
  label,
  required,
  leftSection,
  loading,
  onChange,
  onCommit,
  placeholder,
  value,
  style,
  tableLayout,
  ...props
}: InputProps) {
  const id = useId();
  const ref = useRef<HTMLInputElement>(null);
  const [hasFocus, setHasFocus] = useState(false);
  const [valueOnFocus, setValueOnFocus] = useState<string | undefined>(
    undefined
  );
  // we use this to track changes and toggle the clear action
  const [text, setText] = useUncontrolled({
    value,
    defaultValue,
    onChange,
  });

  function commit(value: string) {
    if (valueOnFocus !== value) {
      onCommit?.(value);
      setValueOnFocus(value);
    }
  }

  let rightSection: ReactNode | undefined;
  if (loading) {
    rightSection = <LoadingIndicator data-testid="input-loading-indicator" />;
  } else if (clearable && !disabled && text != null && text.length > 0) {
    rightSection = (
      <ClearButton
        onClear={() => {
          if (ref.current && ref.current.value !== '') {
            ref.current.value = '';
            setText('');
            commit('');
          }
        }}
      />
    );
  } else if (props.rightSection) {
    rightSection = props.rightSection;
  }

  return (
    <div className={clsx({ [styles['root--table-layout']]: tableLayout })}>
      <FormLabel
        id={id}
        required={required}
        disabled={disabled}
        loading={loading}
        error={error}
        inline={inlineError}
        label={label}
      />
      <MantineInput
        style={style}
        id={id}
        data-testid={dataTestId}
        disabled={disabled || loading}
        error={error}
        leftSection={leftSection}
        readOnly={readOnly}
        onFocus={() => {
          setHasFocus(true);
          setValueOnFocus((ref.current && ref.current.value) ?? undefined);
        }}
        onBlur={() => {
          commit(text);
          setHasFocus(false);
        }}
        onChange={({ target }) => setText(target.value)}
        onKeyUp={({ code }) => {
          if (code === KeyCode.ENTER) {
            // blur and thus trigger commit hook
            ref.current?.blur();
          }
        }}
        placeholder={placeholder}
        ref={ref}
        rightSection={rightSection}
        // title={label} @todo
        unstyled
        value={text}
        classNames={{
          wrapper: clsx(
            styles['wrapper'],
            hasFocus && styles['wrapper--is-focussed']
          ),
          input: styles['input'],
          section: styles['section'],
        }}
      />
    </div>
  );
}
